import React, { useState, useEffect, useCallback } from 'react';
import {
  Select,
  Input,
  Tag,
  Tooltip,
  Typography,
  Row,
  Col,
  Card,
  Badge,
} from 'antd';
import DataTable from '@/components/DataTable';
import { toast } from 'react-toastify';
import Icons from '@/components/third-party/Icons';
import { formatDateTime } from '@/utils/datetime';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { get_outbound_events } from '@/utils/api';
import StatCard from '@/components/StatCard';
import PageHeader from '@/components/PageHeader';
import PageModal from '@/components/PageModal';
import { Workflow, Radio, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const { Text } = Typography;

const EVENT_CATEGORIES = [
  { label: 'All Categories', value: '' },
  { label: 'Config Event', value: 'config_event' },
  { label: 'Batch Event', value: 'batch_event' },
];

const PUBLISHED_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Published', value: 'true' },
  { label: 'Unpublished', value: 'false' },
];

export default function EventLogs() {
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const [eventCategory, setEventCategory] = useState('');
  const [eventType, setEventType] = useState('');
  const [published, setPublished] = useState('');
  const [searchvalue, setSearchvalue] = useState('');

  const [payloadModal, setPayloadModal] = useState({ open: false, payload: null, eventType: '' });

  const fetchEvents = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    const params = { page_number: pageNumber, page_size: pageSize };
    if (eventCategory) params.event_category = eventCategory;
    if (eventType) params.event_type = eventType;
    if (published !== '') params.published = published;
    if (searchvalue) params.search = searchvalue;

    get_outbound_events((res) => {
      setLoading(false);
      if (res?.data?.status === 'success') {
        setData(res.data.data || []);
        setTotalCount(res.data.total_count || 0);
      } else {
        setLoadError(true);
        toast.error(res?.data?.message || 'Failed to fetch event logs');
      }
    }, params);
  }, [pageNumber, pageSize, eventCategory, eventType, published, searchvalue]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const publishedCount = data.filter((d) => d.published).length;
  const pendingCount = data.filter((d) => !d.published).length;
  const errorCount = data.filter((d) => d.last_error).length;

  const allColumns = [
    {
      key: 'event_type',
      title: 'Event Type',
      dataIndex: 'event_type',
      defaultVisible: true,
      alwaysVisible: false,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) => (
        <Text strong style={{ color: textColor, fontSize: 12 }}>
          {val}
        </Text>
      ),
    },
    {
      key: 'event_category',
      title: 'Category',
      dataIndex: 'event_category',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) =>
        val ? (
          <Tag style={{ fontSize: 11 }}>
            {val}
          </Tag>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'published',
      title: 'Status',
      dataIndex: 'published',
      defaultVisible: true,
      sortable: false,
      align: 'center',
      render: (val) =>
        val ? (
          <Badge status="success" text={<span style={{ fontSize: 12, color: '#16a34a' }}>Published</span>} />
        ) : (
          <Badge status="default" text={<span style={{ fontSize: 12, color: '#6b7280' }}>Pending</span>} />
        ),
    },
    {
      key: 'published_at',
      title: 'Published At',
      dataIndex: 'published_at',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) =>
        val ? (
          <Text style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#374151' }}>
            {formatDateTime(val, 'DD MMM YYYY HH:mm:ss')}
          </Text>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'created_at',
      title: 'Created At',
      dataIndex: 'created_at',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'left',
      render: (val) => (
        <Text style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#374151' }}>
          {formatDateTime(val, 'DD MMM YYYY HH:mm:ss')}
        </Text>
      ),
    },
    {
      key: 'request_id',
      title: 'Request ID',
      dataIndex: 'request_id',
      defaultVisible: true,
      sortable: false,
      align: 'left',
      render: (val) =>
        val ? (
          <Text copyable style={{ fontSize: 11, color: defaultColors.primary }}>
            {val}
          </Text>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'last_error',
      title: 'Last Error',
      dataIndex: 'last_error',
      defaultVisible: true,
      sortable: false,
      align: 'left',
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <Tag color="red" style={{ fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
              {val.length > 22 ? val.slice(0, 22) + '…' : val}
            </Tag>
          </Tooltip>
        ) : (
          <span style={{ color: '#9ca3af' }}>—</span>
        ),
    },
    {
      key: 'payload',
      title: 'Payload',
      dataIndex: 'payload',
      defaultVisible: true,
      alwaysVisible: true,
      sortable: false,
      align: 'center',
      width: 80,
      render: (payload, record) => (
        <Tooltip title="View Payload">
          <Icons.Eye
            className="w-5 h-5 cursor-pointer hover:scale-110 transition-transform"
            style={{ color: defaultColors.primary }}
            aria-label="View payload"
            onClick={() => setPayloadModal({ open: true, payload, eventType: record.event_type })}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
    <Card
      className="border border-gray-300 shadow-md"
      bordered={false}
      classNames={{ body: 'page-card-scroll' }}
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff', height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
      title={
        <PageHeader
          compact
          icon={<Icons.Workflow size={22} />}
          title="Event Logs"
          subtitle={`${totalCount.toLocaleString()} events`}
          description="Outbound events the system raised for other systems, whether each was published, and the payload sent."
          filters={
            <>
              <Select
                style={{ width: 160 }}
                aria-label="Category"
                placeholder="Category"
                options={EVENT_CATEGORIES}
                value={eventCategory || undefined}
                allowClear
                onChange={(val) => { setEventCategory(val || ''); setPageNumber(1); }}
              />
              <Input
                style={{ width: 170 }}
                aria-label="Event type"
                placeholder="Event Type..."
                value={eventType}
                allowClear
                onChange={(e) => { setEventType(e.target.value); setPageNumber(1); }}
              />
              <Select
                style={{ width: 140 }}
                aria-label="Status"
                placeholder="Status"
                options={PUBLISHED_OPTIONS}
                value={published !== '' ? published : undefined}
                allowClear
                onChange={(val) => { setPublished(val ?? ''); setPageNumber(1); }}
              />
            </>
          }
        />
      }
    >
      {/* KPI Row */}
      <Row gutter={[12, 12]} className="mb-3">
        <Col xs={12} sm={6}>
          <StatCard
            icon={<Radio />}
            label="Total Events"
            value={totalCount}
            bgColor={`${defaultColors.primary}22`}
            iconColor={defaultColors.primary}
          />
        </Col>
        <Col xs={12} sm={6}>
          <StatCard
            icon={<CheckCircle />}
            label="Published"
            value={publishedCount}
            bgColor={`${defaultColors.success}22`}
            iconColor={defaultColors.success}
          />
        </Col>
        <Col xs={12} sm={6}>
          <StatCard
            icon={<Clock />}
            label="Pending"
            value={pendingCount}
            bgColor={`${defaultColors.palette.gray[500]}22`}
            iconColor={defaultColors.palette.gray[500]}
          />
        </Col>
        <Col xs={12} sm={6}>
          <StatCard
            icon={<AlertCircle />}
            label="With Errors"
            value={errorCount}
            bgColor={`${defaultColors.error}22`}
            iconColor={defaultColors.error}
          />
        </Col>
      </Row>

      <DataTable
        className="event-logs-table"
        tableKey="eventLogsTable"
        bordered
        data={data}
        columns={allColumns}
        rowKey="id"
        loading={loading}
        scrollX="max-content"
        height={null}
        minHeight={320}
        serverSide
        searchable
        searchPlaceholder="Search events…"
        searchValue={searchvalue}
        onSearch={(value) => { setSearchvalue(value); setPageNumber(1); }}   // the server does the search
        columnConfig
        showCount={false}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showTotal: (n) => `Total ${n} events`,
          onChange: (page, size) => {
            if (size !== pageSize) { setPageNumber(1); setPageSize(size); } else setPageNumber(page);
          },
        }}
        emptyMessage="No events found"
        error={loadError}
        onRetry={fetchEvents}
      />
      {/* Payload Modal */}
      <PageModal
        open={payloadModal.open}
        icon={<Icons.Eye size={22} />}
        title="Payload"
        description={payloadModal.eventType}
        onCancel={() => setPayloadModal({ open: false, payload: null, eventType: '' })}
        width={640}
        destroyOnHidden
      >
        <pre
          style={{
            backgroundColor: isDark ? '#1e293b' : '#f8fafc',
            color: isDark ? '#e2e8f0' : '#1e293b',
            padding: 16,
            borderRadius: 8,
            fontSize: 13,
            overflow: 'auto',
            maxHeight: 'calc(100dvh - 300px)',
            margin: 0,
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          }}
        >
          {JSON.stringify(payloadModal.payload, null, 2)}
        </pre>
      </PageModal>
    </Card>
    </div>
  );
}
